import React, { useState, useCallback, useEffect } from "react";
import { authFetch, API_BASE } from "../api";

/**
 * Modal: create a new app from a recipe.
 *
 * Two modes:
 *   "exact"     → deploys the recipe templates directly. Fastest, most reliable.
 *                 User should configure the recipe's required secrets first
 *                 (in the API Keys panel of the created app), then the app
 *                 works immediately.
 *   "customize" → deploys templates AND drops into the chat with the recipe
 *                 code pre-loaded, so the user can iterate via prompts.
 *                 (Coming in a future release — disabled for now.)
 *
 * After successful deploy, calls onCreated({ name, url, mode }) so the parent
 * (App.js) can navigate the user into the new app.
 */
export default function CreateFromRecipeModal({ recipe, onClose, onCreated, verifiedSecrets }) {
  // Default name based on recipe ID — user can edit
  const [appName, setAppName] = useState(() => `my-${recipe.id}`);
  const [mode, setMode] = useState("exact");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // Show a per-step progress indicator so users know what's happening
  // when the "Create" button is pressed (deploy can take ~20 seconds)
  const [phase, setPhase] = useState(null); // "deploying" | "saving-keys" | null
  // User can opt out of auto-saving the verified secrets (e.g., if they want
  // to use different creds in production than they tested with)
  const [saveVerifiedSecrets, setSaveVerifiedSecrets] = useState(true);

  const hasVerifiedSecrets = verifiedSecrets && Object.keys(verifiedSecrets).length > 0;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !creating) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, creating]);

  const sanitizeName = (name) =>
    name
      .replace(/[^a-z0-9-]/gi, "-")
      .toLowerCase()
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const create = useCallback(async () => {
    const cleanName = sanitizeName(appName);
    if (!cleanName) {
      setError("App name must contain at least one letter or number");
      return;
    }

    setCreating(true);
    setError(null);
    setPhase("deploying");
    try {
      // Step 1: pull the recipe's template files
      const recipeRes = await authFetch(`${API_BASE}/recipes/${recipe.id}/create-app`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cleanName }),
      });
      if (!recipeRes.ok) {
        const body = await recipeRes.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load recipe (HTTP ${recipeRes.status})`);
      }
      const { files } = await recipeRes.json();
      if (!files || Object.keys(files).length === 0) {
        throw new Error("Recipe has no deployable templates");
      }

      // Step 2: deploy as a preview using the scaffold format (files object)
      const deployRes = await authFetch(`${API_BASE}/deploy/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: cleanName, files }),
      });
      if (!deployRes.ok) {
        const body = await deployRes.json().catch(() => ({}));
        throw new Error(body.error || `Deploy failed (HTTP ${deployRes.status})`);
      }
      const deployData = await deployRes.json();

      // Step 3 (optional): persist the verified credentials to the new app's
      // secret store. This closes the "I tested keys but now the app has none"
      // onboarding gap. If it fails we still hand the app over — the user
      // can manually save keys via API Keys as before.
      let secretsSaved = false;
      if (hasVerifiedSecrets && saveVerifiedSecrets) {
        setPhase("saving-keys");
        try {
          const saveRes = await authFetch(`${API_BASE}/secrets/${cleanName}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ secrets: verifiedSecrets }),
          });
          if (saveRes.ok) {
            secretsSaved = true;
          } else {
            console.warn("[recipe] failed to auto-save secrets after deploy:", saveRes.status);
          }
        } catch (saveErr) {
          console.warn("[recipe] auto-save secrets threw:", saveErr.message);
        }
      }

      // Step 4: hand off to the parent to finish the UX (navigate, open chat, etc.)
      if (onCreated) {
        onCreated({
          name: cleanName,
          url: deployData.url,
          mode,
          recipeId: recipe.id,
          files,
          secretsSaved,
        });
      }
      onClose();
    } catch (err) {
      setError(err.message || "Create failed");
    } finally {
      setCreating(false);
      setPhase(null);
    }
  }, [recipe.id, appName, mode, onCreated, onClose, hasVerifiedSecrets, saveVerifiedSecrets, verifiedSecrets]);

  return (
    <div className="recipe-modal-backdrop" onClick={!creating ? onClose : undefined}>
      <div className="recipe-modal recipe-modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="recipe-modal-header">
          <h2>Create dashboard from {recipe.name}</h2>
          <button className="recipe-modal-close" onClick={onClose} disabled={creating} aria-label="Close">×</button>
        </div>

        <div className="recipe-modal-body">
          <div className="recipe-modal-field">
            <label>App name</label>
            <input
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              onBlur={() => setAppName(sanitizeName(appName))}
              disabled={creating}
              placeholder="my-powerbi-dashboard"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <div className="recipe-modal-field">
            <label>Create mode</label>
            <label className="recipe-modal-radio">
              <input
                type="radio"
                name="mode"
                value="exact"
                checked={mode === "exact"}
                onChange={() => setMode("exact")}
                disabled={creating}
              />
              <div>
                <strong>Use recipe as-is</strong>
                <span>Deploys the tested template directly. ~30 seconds to working dashboard. Recommended.</span>
              </div>
            </label>
            <label className="recipe-modal-radio" style={{ opacity: 0.6 }}>
              <input
                type="radio"
                name="mode"
                value="customize"
                checked={false}
                disabled
              />
              <div>
                <strong>Customize with AI (coming soon)</strong>
                <span>Will load templates as a starting point and open the chat for iterative tweaks.</span>
              </div>
            </label>
          </div>

          {error && <div className="recipe-modal-error">{error}</div>}

          {/* Verified-secrets auto-save: the main payoff of the test modal */}
          {hasVerifiedSecrets ? (
            <div className="recipe-modal-field" style={{ marginTop: 16 }}>
              <label className="recipe-modal-radio" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={saveVerifiedSecrets}
                  onChange={(e) => setSaveVerifiedSecrets(e.target.checked)}
                  disabled={creating}
                />
                <div>
                  <strong>
                    Save my {Object.keys(verifiedSecrets).length} tested keys to this app
                  </strong>
                  <span>
                    The keys you just verified in Test Credentials will be stored on{" "}
                    <code>{sanitizeName(appName) || "this app"}</code> so the dashboard works
                    immediately — no manual setup.
                  </span>
                </div>
              </label>
            </div>
          ) : (
            <p className="recipe-modal-desc" style={{ marginTop: 16 }}>
              <strong>Tip:</strong> Run <em>Test Credentials</em> first — if it passes, your
              keys will be saved to the new app automatically when you create it.
            </p>
          )}
        </div>

        <div className="recipe-modal-footer">
          <button onClick={onClose} disabled={creating}>Cancel</button>
          <button
            className="recipe-modal-primary"
            onClick={create}
            disabled={creating || !appName.trim()}
          >
            {phase === "deploying"
              ? "Deploying…"
              : phase === "saving-keys"
                ? "Saving keys…"
                : creating
                  ? "Creating…"
                  : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
